// useQueryStringBuilder.ts
'use client'

import { isNil } from 'lodash'

interface UseQueryStringBuilderReturn {
  updateQueryString: (baseUrl: string, newParams: QueryStringValues) => string
}

type QueryStringValues = Record<string, string | boolean | number | null>

/**
 * A hook that, given a base URL (path + optional query string), returns a function
 * to add, update, or remove multiple query parameters on that URL at once.
 *
 * @example
 *   const { updateQueryString } = useQueryStringBuilder();
 *   const newUrl = updateQueryString(
 *     '/bookings?flow=returing&isSelfPay=true',
 *     { flow: 'new', name: 'John' }
 *   );
 *   // newUrl === '/bookings?flow=new&isSelfPay=true&name=John'
 *
 *   const removedUrl = updateQueryString(
 *     '/bookings?flow=returing&isSelfPay=true',
 *     { flow: null, isSelfPay: null }
 *   );
 *   // removedUrl === '/bookings'
 */
export function useQueryStringBuilder(): UseQueryStringBuilderReturn {
  /**
   * Updates multiple query parameters in the given baseUrl.
   *
   * @param baseUrl       The original URL (path + optional query string).
   *
   * @param params     An object whose keys are parameter names and whose values
   *                       are the values to set. If a value is `null` or `''`, the
   *                       parameter is removed.
   *
   * @returns A new string combining the path and the updated query string,
   *          or just the path if no query parameters remain.
   */
  const updateQueryString = (
    baseUrl: string,
    params: QueryStringValues
  ): string => {
    // Split baseUrl into path and existing query string (if present)
    const [pathPart, queryPart] = baseUrl.split('?')
    const existingParams = new URLSearchParams(queryPart || '')

    // Iterate through keys in newParams
    Object.entries(params).forEach(([key, value]) => {
      if (isNil(value) || value === '') {
        existingParams.delete(key)
      } else {
        existingParams.set(key, String(value))
      }
    })

    const updatedQuery = existingParams.toString()
    return updatedQuery ? `${pathPart}?${updatedQuery}` : pathPart
  }

  return { updateQueryString }
}
